import { useEffect, useRef, useState } from 'react';
import { useMatches } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { getAppEnv } from '~portal/env';
import { logger } from '~portal/lib';
import { appMode } from '~portal/lib/app-mode';
import { useAnalyticsConsent } from '~portal/lib/cookie-consent';
import { type DatadogSdk, loadDatadog, loadedDatadog, onDatadogLoaded } from './datadog-sdk';

// Build-time flag; Datadog instrumentation is skipped entirely when not 'true'.
const DD_ENABLED = import.meta.env.VITE_DD_ENABLED === 'true';

// The SDKs can only be initialised once per page; a withdrawn-then-restored consent must resume
// the existing instance rather than call `init` again. The SDK's own record is the source of truth.
const isInitialised = (sdk: DatadogSdk | null): sdk is DatadogSdk =>
  sdk !== null && sdk.datadogRum.getInitConfiguration() !== undefined;

/**
 * Datadog Real User Monitoring (RUM) and Browser Logs initialisation component
 *
 * Initialises Datadog RUM and Browser Logs for browser monitoring in Vite-based React applications.
 * Must run as client-side effect (useEffect hook).
 *
 * The application will start normally even if Datadog is not configured.
 * Instrumentation is gracefully skipped when disabled or missing configuration.
 *
 * The SDKs are not in the entry chunk (round 3 lane 8): the first consent with Datadog enabled
 * loads them (`datadog-sdk.ts`), so a visitor who never consents never downloads them.
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/setup
 * @see https://docs.datadoghq.com/logs/log_collection/javascript/
 */
export function DatadogRUM() {
  const { isLoading, user } = useAuth();
  // Consent-gated (H2-204): RUM and Logs set `_dd_s` and record sessions, which is exactly what
  // the banner's `analytics` category covers. Nothing is initialised until the visitor accepts;
  // withdrawal ends the session, mirroring the legacy site.
  const analyticsConsent = useAnalyticsConsent();
  // Mirrors the backend's internal user id resolution (`externalId ?? sub`) so RUM
  // sessions continue under the same identity across the Clerk-to-WorkOS migration.
  const userId = user?.externalId || user?.id;

  // The SDKs once this component has initialised (or resumed) them; drives the user context.
  const [sdk, setSdk] = useState<DatadogSdk | null>(() => {
    const loaded = loadedDatadog();
    return isInitialised(loaded) ? loaded : null;
  });

  // Initialise Datadog on first analytics consent; stop/resume the session on later changes
  useEffect(() => {
    if (!DD_ENABLED) {
      return;
    }

    if (!analyticsConsent) {
      const loaded = loadedDatadog();
      if (isInitialised(loaded)) {
        loaded.datadogRum.stopSession();
        logger.debug('[Datadog] Session stopped: analytics consent withdrawn');
      }
      return;
    }

    // Get configuration from validated environment
    const { datadog } = getAppEnv();

    // Validate required configuration before fetching the SDKs at all
    if (!datadog) {
      // Log warning in development, silent in production
      console.warn(
        '[Datadog] Enabled but missing required configuration. Skipping initialisation.',
      );
      return;
    }

    let cancelled = false;
    loadDatadog()
      .then((loaded) => {
        if (cancelled) return;
        const { datadogRum, datadogLogs } = loaded;

        if (isInitialised(loaded)) {
          datadogRum.startSessionReplayRecording();
          logger.debug('[Datadog] Session replay resumed: analytics consent restored');
          setSdk(loaded);
          return;
        }

        // Use mode-aware service name for distinct RUM sessions per variant
        const serviceName = `h2gc-portal-${appMode}`;

        const common = {
          clientToken: datadog.clientToken,
          site: datadog.site,
          service: serviceName,
          env: datadog.env,
          // Use git commit SHA as version for session tagging
          version: import.meta.env.VITE_GIT_SHA || 'unknown',
          sessionSampleRate: 100,
        };

        // Initialise Datadog Browser Logs
        datadogLogs.init({
          ...common,
          forwardErrorsToLogs: true, // Automatically forward console errors
          forwardConsoleLogs: ['error'], // Forward console.error() to Datadog
          sessionSampleRate: 100,
          // Enable trace correlation with RUM and backend APM
          beforeSend: (log) => {
            // Add RUM context to logs for correlation
            const rumContext = datadogRum.getInternalContext();
            if (rumContext) {
              log.dd_trace_id = rumContext.trace_id;
              log.dd_span_id = rumContext.span_id;
            }
            return true;
          },
        });

        // Initialise Datadog RUM
        datadogRum.init({
          ...common,
          applicationId: datadog.applicationId,
          sessionSampleRate: 100, // Track 100% of sessions
          sessionReplaySampleRate: 100, // Capture 100% of session replays
          trackUserInteractions: true, // Track clicks, inputs, etc.
          trackResources: true, // Track XHR, fetch, and other resources
          trackLongTasks: true, // Track performance issues
          trackViewsManually: true, // Manual view tracking via DatadogViewTracker
          defaultPrivacyLevel: 'mask-user-input', // Privacy: mask user input by default
          // Enable trace correlation with backend APM
          allowedTracingUrls: [
            {
              match: import.meta.env.VITE_BACKEND_API_URL,
              propagatorTypes: ['tracecontext', 'datadog'], // W3C + Datadog formats
            },
          ],
        });

        // Start RUM session
        datadogRum.startSessionReplayRecording();
        setSdk(loaded);

        logger.debug('[Datadog] Initialised RUM and Browser Logs successfully', {
          service: serviceName,
          env: datadog.env,
          version: import.meta.env.VITE_GIT_SHA || 'unknown',
        });
      })
      .catch((err: unknown) => {
        // Degraded, not broken: the page works without monitoring. A later consent retries.
        logger.warn('[Datadog] SDK failed to load; RUM and Logs are off for this page', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [analyticsConsent]);

  // Stamp the richest available identity onto Datadog events once AuthKit settles.
  // setUser replaces the whole user object.
  useEffect(() => {
    if (!DD_ENABLED || isLoading || !analyticsConsent || !sdk) {
      return;
    }
    const { datadogRum, datadogLogs } = sdk;

    if (!userId) {
      datadogRum.clearUser();
      datadogLogs.clearUser();
      logger.debug('[Datadog] User context cleared');
      return;
    }

    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined;
    const userContext = {
      id: userId,
      email: user?.email,
      name,
    };

    datadogRum.setUser(userContext);
    datadogLogs.setUser(userContext);

    logger.debug('[Datadog] User context set', { userId, enriched: Boolean(user) });
  }, [userId, isLoading, user, analyticsConsent, sdk]);

  // This component renders nothing
  return null;
}

/**
 * Datadog view tracking component for TanStack Router
 *
 * Tracks route changes and reports them to Datadog RUM as views.
 * Uses route patterns (e.g., /chat/:conversationId) rather than full URLs
 * to enable meaningful aggregation of analytics.
 *
 * Must be rendered inside RouterProvider to access router context.
 */
export function DatadogViewTracker() {
  const matches = useMatches();
  const { user } = useAuth();
  const userId = user?.externalId || user?.id;
  // The view last reported to RUM; set only once the (lazily loaded) SDK has taken it.
  const startedViewRef = useRef<string | null>(null);

  useEffect(() => {
    if (!DD_ENABLED) {
      return;
    }

    // Get the deepest matched route (most specific)
    const deepestMatch = matches[matches.length - 1];
    if (!deepestMatch) {
      return;
    }

    // Convert TanStack route ID to a readable view name
    // e.g., "/_protected/chat/$conversationId" -> "/chat/:conversationId"
    const routeId = deepestMatch.routeId;
    const viewName = routeId
      .replace(/^\/_protected/, '') // Remove layout prefix
      .replace(/\$(\w+)/g, ':$1') // Convert $param to :param (Datadog convention)
      .replace(/^$/, '/'); // Handle root route

    // Only track if route changed (avoid duplicate views on re-renders)
    if (viewName === startedViewRef.current) {
      return;
    }

    // Start a new RUM view for this route once the SDK is there (at once if it already is). A
    // route change before it loads drops the stale view: only the current one is reported.
    return onDatadogLoaded(({ datadogRum }) => {
      startedViewRef.current = viewName;
      datadogRum.startView({ name: viewName });

      // Re-stamp the user id on the new view as a safety net, in case the first
      // events of this view flush before the id is set globally. setUserProperty
      // updates only `usr.id`, preserving any name/email already enriched.
      if (userId) {
        datadogRum.setUserProperty('id', userId);
      }

      logger.debug('[Datadog] View tracked', { viewName, routeId });
    });
  }, [matches, userId]);

  return null;
}
